'use client';

import type { EntitlementsInfo, StripePromoCoupon, StripeStoreProduct } from '@inkitt/contracts-common';
import type { CountryCode } from '@inkitt/geo';
import { useEffect, useState } from 'react';
import { trackClientInfo } from '../../../../../../services/tracking/trackClientInfo';
import { ApplyCodeModal } from './ApplyCodeModal';
import { SubscriptionLayout } from './SubscriptionLayout';

type SubscriptionPageClientProps = {
  subscriptions: StripeStoreProduct[];
  oneTimePackages: StripeStoreProduct[];
  appliedCoupon?: StripePromoCoupon;
  countryCode: CountryCode;
  storyId?: string;
  episodeNumber?: string;
  defaultTab?: string;
  coupon?: string;
  source?: string;
  state?: string;
  trialDays?: number;
  entitlementsInfo: EntitlementsInfo;
  isAnonymous: boolean;
  pointsBalanceSlot: React.ReactNode;
};

export const SubscriptionPageClient: React.FC<SubscriptionPageClientProps> = ({
  subscriptions,
  oneTimePackages,
  appliedCoupon,
  countryCode,
  storyId,
  episodeNumber,
  defaultTab = 'subscription',
  coupon,
  source,
  state,
  trialDays,
  entitlementsInfo,
  isAnonymous,
  pointsBalanceSlot,
}) => {
  const [activeTab, setActiveTab] = useState<'subscription' | 'points'>(
    defaultTab === 'points' ? 'points' : 'subscription',
  );
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);

  useEffect(() => {
    trackClientInfo('Subscription Page View', {
      tab: activeTab,
      storyId,
      episodeNumber,
      source,
      hasCoupon: !!coupon,
      state,
    });
  }, [activeTab, storyId, episodeNumber, source, coupon, state]);

  const handleTabChange = (tab: 'subscription' | 'points') => {
    setActiveTab(tab);
    trackClientInfo('paywall-tab-clicked', { tab });

    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);
    window.history.replaceState({}, '', url.toString());
  };

  const handleClearCoupon = () => {
    trackClientInfo('paywall-promo_code-cleared', { coupon });

    const url = new URL(window.location.href);
    url.searchParams.delete('coupon');
    window.location.href = url.toString();
  };

  const handleOpenPromoModal = () => {
    setIsPromoModalOpen(true);
  };

  const handleClosePromoModal = () => {
    setIsPromoModalOpen(false);
  };

  const handleApplyPromoCode = (code: string) => {
    trackClientInfo('paywall-promo_code-applied', { coupon: code });

    const url = new URL(window.location.href);
    url.searchParams.set('coupon', code);
    window.location.href = url.toString();
  };

  return (
    <>
      <SubscriptionLayout
        subscriptions={subscriptions}
        oneTimePackages={oneTimePackages}
        appliedCoupon={appliedCoupon}
        countryCode={countryCode}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        storyId={storyId}
        episodeNumber={episodeNumber}
        coupon={coupon}
        source={source}
        state={state}
        trialDays={trialDays}
        entitlementsInfo={entitlementsInfo}
        isAnonymous={isAnonymous}
        onClearCoupon={handleClearCoupon}
        onOpenPromoModal={handleOpenPromoModal}
        pointsBalanceSlot={pointsBalanceSlot}
      />
      <ApplyCodeModal
        isOpen={isPromoModalOpen}
        onClose={handleClosePromoModal}
        onApply={handleApplyPromoCode}
        countryCode={countryCode}
      />
    </>
  );
};
